/**
 * InfoModal — rich informational modals ported from old modals.js.
 * Used for: About, Akash Provider, ICP, Model, Browser Storage.
 * Now includes full content: external links, technical details, code blocks, lists.
 */
import styles from '../../styles/components/Modal.module.css';

export type InfoVariant = 'about' | 'akash' | 'icp' | 'model' | 'storage';

interface InfoModalProps {
  variant: InfoVariant;
  /** Extra data from health endpoint (model, provider, gpu) */
  data?: Record<string, unknown>;
  onClose: () => void;
}

const CANISTER_ID = 'zc67k-kiaaa-aaaal-qtmiq-cai';
const BACKEND_CANISTER_ID = 'au5zq-2qaaa-aaaal-qtowa-cai';

const monoStyle: React.CSSProperties = {
  background: '#1a1a1a',
  padding: '12px',
  borderRadius: '6px',
  fontSize: '11px',
  lineHeight: 1.8,
  fontFamily: 'monospace',
  wordBreak: 'break-all',
  marginTop: '8px',
};

const listStyle: React.CSSProperties = {
  margin: '8px 0 0 0',
  paddingLeft: '20px',
  color: '#aaa',
  fontSize: '12px',
  lineHeight: 1.6,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ margin: '0 0 6px 0', color: 'var(--color-text-primary)', fontSize: '0.9rem' }}>
        {title}
      </h4>
      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
        {children}
      </div>
    </div>
  );
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#69db7c', textDecoration: 'none' }}
    >
      {children} ↗
    </a>
  );
}

function renderAbout() {
  return {
    title: 'About W AI',
    subtitle: 'Free AI chat. No account required.',
    content: (
      <>
        <Section title="Frontend — ICP Canister">
          <p style={{ margin: 0 }}>
            This interface is served from an upgradeable Internet Computer canister. ICP can certify asset responses, while the canister controllers retain the ability to deploy product updates.
          </p>
        </Section>
        <Section title="Inference — Akash Network">
          <p style={{ margin: 0 }}>
            An open-weight model runs on an Akash provider. Prompts are processed by Dubya&apos;s
            backend and the selected infrastructure provider, so do not submit sensitive data.
          </p>
        </Section>
        <Section title="Guest Access">
          <p style={{ margin: 0 }}>
            No account, wallet, or payment is required. You can send up to 100 messages in a
            rolling 24-hour period. Rapid sends and repeated prompts are temporarily throttled.
          </p>
        </Section>
        <Section title="Browser Storage">
          <p style={{ margin: 0 }}>
            Conversations are saved in this browser and restored when you return. Clearing this
            site&apos;s browser data removes saved chats. Use Export to keep a separate copy.
          </p>
        </Section>
        <Section title="Data Flow">
          <pre style={{ ...monoStyle, overflowX: 'auto', whiteSpace: 'pre' }}>
{`Browser storage ──conversation context──▶ AI backend ──▶ Model`}
          </pre>
          <p style={{ marginTop: '8px' }}>
            Current beta warning: the active provider ingress does not present a publicly trusted
            TLS certificate to Cloudflare. Do not submit private, regulated, or sensitive content;
            trusted origin transport is required before paid launch.
          </p>
        </Section>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <ExternalLink href="https://internetcomputer.org">ICP</ExternalLink>
          {' · '}
          <ExternalLink href="https://akash.network">Akash</ExternalLink>
        </div>
      </>
    ),
  };
}

function renderAkash(data: Record<string, unknown>) {
  const provider = String(data.provider ?? 'Unknown');
  const gpuType = String(data.gpu_type ?? 'Unknown');
  const model = String(data.model ?? 'Unknown');

  return {
    title: 'Akash Provider',
    subtitle: 'Your AI runs on decentralized compute',
    content: (
      <>
        <Section title={`Provider: ${provider}`}>
          <p style={{ margin: 0 }}>
            This is the Akash provider serving the current inference lease. Provider identity and advertised hardware come from deployment metadata; this screen does not independently certify its security or audit status.
          </p>
        </Section>
        <Section title="Provider Due Diligence">
          <p style={{ margin: 0 }}>
            Before a production lease is selected, Dubya reviews the provider&apos;s advertised hardware, location, pricing, certificate, and operating history. Marketplace metadata is not a guarantee.
          </p>
        </Section>
        <Section title="Your Session">
          <div style={monoStyle}>
            <div><span style={{ color: '#888' }}>Provider:</span> <span style={{ color: '#ff6b6b' }}>{provider}</span></div>
            <div><span style={{ color: '#888' }}>Compute:</span> <span style={{ color: '#69db7c' }}>{gpuType}</span></div>
            <div><span style={{ color: '#888' }}>Model:</span> <span style={{ color: '#a78bfa' }}>{model}</span></div>
          </div>
        </Section>
        <Section title="Why Decentralized Compute?">
          <p style={{ margin: 0 }}>
            Akash is a marketplace where independent providers can offer compute. That gives the application more provider choice than a single-cloud deployment, while Dubya operators still control this service and its access policies.
          </p>
        </Section>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <ExternalLink href="https://console.akash.network/providers">View All Providers</ExternalLink>
        </div>
      </>
    ),
  };
}

function renderICP() {
  return {
    title: 'Internet Computer',
    subtitle: 'Your frontend runs on the world computer',
    content: (
      <>
        <Section title="What is ICP?">
          <p style={{ margin: 0 }}>
            The Internet Computer is a blockchain that runs at web speed. Unlike traditional blockchains, it can host entire web applications — frontend, backend, and data — all on-chain.
          </p>
        </Section>
        <Section title="Your Canister">
          <div style={monoStyle}>
            <div><span style={{ color: '#888' }}>Frontend:</span> <span style={{ color: '#5ac8fa' }}>{CANISTER_ID}</span></div>
            <div style={{ marginTop: '4px' }}><span style={{ color: '#888' }}>Backend:</span> <span style={{ color: '#5ac8fa' }}>{BACKEND_CANISTER_ID}</span></div>
          </div>
          <p style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
            Canisters are smart contracts that can serve web content. Your W AI interface is hosted here.
          </p>
        </Section>
        <Section title="Why It Matters">
          <ul style={listStyle}>
            <li><strong>Certified assets:</strong> ICP can provide cryptographic verification for frontend responses</li>
            <li><strong>Distributed hosting:</strong> The canister runs on an ICP subnet rather than one conventional web server</li>
            <li><strong>Upgradeable:</strong> Authorized controllers can publish reviewed releases</li>
            <li><strong>Operational dependency:</strong> Availability still depends on cycles, gateways, DNS, and healthy canisters</li>
          </ul>
        </Section>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <ExternalLink href={`https://dashboard.internetcomputer.org/canister/${CANISTER_ID}`}>View Canister</ExternalLink>
        </div>
      </>
    ),
  };
}

function renderModel(data: Record<string, unknown>) {
  const modelName = String(data.model ?? 'Unknown');
  const gpuType = String(data.gpu_type ?? 'Unknown');

  const isLlama = modelName.toLowerCase().includes('llama');
  const isQwen = modelName.toLowerCase().includes('qwen');
  const is70B = /70b/i.test(modelName) || /72b/i.test(modelName);
  const is14B = /14b/i.test(modelName);
  const is8B = /8b/i.test(modelName);

  let modelFamily = 'Unknown';
  let modelDescription = 'A large language model running on decentralized infrastructure.';
  let modelLink = 'https://huggingface.co';

  if (isLlama) {
    modelFamily = 'Meta Llama';
    modelDescription = "Meta's open-weight Llama model family, designed for local and hosted inference with general reasoning and coding capabilities.";
    modelLink = 'https://llama.meta.com';
  } else if (isQwen) {
    modelFamily = 'Alibaba Qwen';
    modelDescription = "Alibaba's open-weight Qwen model series, designed for multilingual, reasoning, and coding tasks.";
    modelLink = 'https://qwenlm.github.io';
  }

  let sizeInfo = '';
  if (is70B) sizeInfo = '70–72 billion parameters — requires significant GPU memory, offers highest quality responses.';
  else if (is14B) sizeInfo = '14 billion parameters — strong balance of quality and speed.';
  else if (is8B) sizeInfo = '8 billion parameters — efficient and fast while maintaining strong capabilities.';

  return {
    title: 'AI Model',
    subtitle: 'Open-weight AI on Dubya-operated infrastructure',
    content: (
      <>
        <Section title="Current Model">
          <div style={monoStyle}>
            <div><span style={{ color: '#888' }}>Model:</span> <span style={{ color: '#a78bfa' }}>{modelName}</span></div>
            <div><span style={{ color: '#888' }}>Family:</span> <span style={{ color: '#a78bfa' }}>{modelFamily}</span></div>
            <div><span style={{ color: '#888' }}>Compute:</span> <span style={{ color: '#69db7c' }}>{gpuType}</span></div>
          </div>
        </Section>
        <Section title="About This Model">
          <p style={{ margin: 0 }}>{modelDescription}</p>
          {sizeInfo && <p style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>{sizeInfo}</p>}
        </Section>
        <Section title="Why Open-Weight?">
          <ul style={listStyle}>
            <li><strong>Inspectability:</strong> Model weights and upstream documentation are publicly available</li>
            <li><strong>Self-hostable:</strong> The model can run without a proprietary model API</li>
            <li><strong>Operational control:</strong> Dubya selects the model, limits, and application policies</li>
            <li><strong>Data path:</strong> Prompts are processed by the Akash-hosted Dubya backend</li>
          </ul>
        </Section>
        <Section title="Powered by llama.cpp">
          <p style={{ margin: 0 }}>
            W AI uses llama.cpp to run the current model on provider compute. No model API calls are made to OpenAI or Anthropic.
          </p>
        </Section>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <ExternalLink href={modelLink}>Learn More</ExternalLink>
        </div>
      </>
    ),
  };
}

function renderStorage() {
  return {
    title: 'Browser Storage',
    subtitle: 'Your conversations stay available in this browser',
    content: (
      <>
        <Section title="Conversation Memory">
          <p style={{ margin: 0 }}>
            Messages, chat titles, and pins are saved locally. Earlier messages from the selected
            chat are sent with each new prompt so the model can follow the conversation.
          </p>
        </Section>
        <Section title="Keeping a Copy">
          <p style={{ margin: 0 }}>
            Chats return after a refresh or reopening the site on this browser. They do not sync
            between devices. Clearing this site&apos;s browser data removes them; Export saves a
            separate copy.
          </p>
        </Section>
      </>
    ),
  };
}

export function InfoModal({ variant, data = {}, onClose }: InfoModalProps) {
  const renderers: Record<InfoVariant, () => { title: string; subtitle: string; content: React.ReactNode }> = {
    about: renderAbout,
    akash: () => renderAkash(data),
    icp: renderICP,
    model: () => renderModel(data),
    storage: renderStorage,
  };

  const { title, subtitle, content } = renderers[variant]();

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div className={styles.body}>
          <p style={{
            textAlign: 'center',
            color: '#aaa',
            marginBottom: '20px',
            fontSize: '0.85rem',
          }}>
            {subtitle}
          </p>
          {content}
        </div>
        <div className={styles.footer}>
          <button className={styles.primaryBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default InfoModal;
